import { useCallback, useEffect, useRef, useState } from "react";

/** A finger that travels farther than this between touching down and lifting is scrolling, not tapping. */
const TAP_SLOP_PX = 10;

export interface UseLeaveDragSelectParams {
  cellWidth: number;
  dayCount: number;
  hScrollRef: React.RefObject<HTMLDivElement>;
  onCommit: (userId: string, startIdx: number, endIdx: number) => void;
  /** Whose row takes a finger selection; taps on other rows are left to the browser to scroll. */
  touchUserId: string;
}

interface DaySelection {
  userId: string;
  startIdx: number;
  endIdx: number;
}

/**
 * Maps pointer X to day index using the horizontal scroll container’s viewport
 * (header + grid share the same scrollLeft).
 *
 * A mouse or pen drags across the days. A finger cannot drag (the browser takes the gesture to scroll the
 * calendar), so it taps the first day and then the last day; a single day is a tap on the same day twice.
 */
export function useLeaveDragSelect({
  cellWidth,
  dayCount,
  hScrollRef,
  onCommit,
  touchUserId,
}: UseLeaveDragSelectParams) {
  const [preview, setPreview] = useState<DaySelection | null>(null);
  // The first day of a tapped range, shown until the second tap or a cancel.
  const [tapStart, setTapStart] = useState<{ userId: string; dayIdx: number } | null>(null);

  const dragRef = useRef<{
    userId: string;
    anchor: number;
    pointerId: number;
  } | null>(null);

  const rangeRef = useRef<DaySelection | null>(null);

  const touchRef = useRef<{
    userId: string;
    dayIdx: number;
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);

  const tapStartRef = useRef(tapStart);

  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const clientXToDayIndex = useCallback(
    (clientX: number) => {
      const scrollEl = hScrollRef.current;
      if (!scrollEl || cellWidth <= 0) return -1;
      const rect = scrollEl.getBoundingClientRect();
      const x = clientX - rect.left + scrollEl.scrollLeft;
      const idx = Math.floor(x / cellWidth);
      if (idx < 0 || idx >= dayCount) return -1;
      return idx;
    },
    [cellWidth, dayCount, hScrollRef]
  );

  const changeTapStart = useCallback((next: { userId: string; dayIdx: number } | null) => {
    tapStartRef.current = next;
    setTapStart(next);
  }, []);

  const handleTap = useCallback(
    (userId: string, dayIdx: number) => {
      const start = tapStartRef.current;
      if (!start || start.userId !== userId) {
        changeTapStart({ userId, dayIdx });
        return;
      }
      changeTapStart(null);
      onCommitRef.current(userId, Math.min(start.dayIdx, dayIdx), Math.max(start.dayIdx, dayIdx));
    },
    [changeTapStart]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const idx = clientXToDayIndex(e.clientX);
      if (idx < 0) return;
      const startIdx = Math.min(d.anchor, idx);
      const endIdx = Math.max(d.anchor, idx);
      const next = { userId: d.userId, startIdx, endIdx };
      rangeRef.current = next;
      setPreview(next);
    };

    const onUp = (e: PointerEvent) => {
      const touch = touchRef.current;
      if (touch && touch.pointerId === e.pointerId) {
        touchRef.current = null;
        const travelled = Math.hypot(e.clientX - touch.x, e.clientY - touch.y);
        if (travelled <= TAP_SLOP_PX) handleTap(touch.userId, touch.dayIdx);
        return;
      }

      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      dragRef.current = null;
      const r = rangeRef.current;
      rangeRef.current = null;
      setPreview(null);
      if (r) {
        onCommitRef.current(r.userId, r.startIdx, r.endIdx);
      }
    };

    // The browser cancels the pointer when it turns the gesture into a scroll: that is not a selection.
    const onCancel = (e: PointerEvent) => {
      if (touchRef.current?.pointerId === e.pointerId) touchRef.current = null;
      if (dragRef.current?.pointerId === e.pointerId) {
        dragRef.current = null;
        rangeRef.current = null;
        setPreview(null);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [clientXToDayIndex, handleTap]);

  const onRowPointerDown = useCallback(
    (userId: string, e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const el = e.target as HTMLElement;
      if (el.closest("[data-leave-segment]")) return;
      const idx = clientXToDayIndex(e.clientX);
      if (idx < 0) return;

      if (e.pointerType === "touch") {
        // No preventDefault: the finger must stay free to scroll the calendar.
        if (userId !== touchUserId) return;
        touchRef.current = { userId, dayIdx: idx, pointerId: e.pointerId, x: e.clientX, y: e.clientY };
        return;
      }

      e.preventDefault();
      dragRef.current = { userId, anchor: idx, pointerId: e.pointerId };
      const initial = { userId, startIdx: idx, endIdx: idx };
      rangeRef.current = initial;
      setPreview(initial);
    },
    [clientXToDayIndex, touchUserId]
  );

  const cancelTapSelection = useCallback(() => changeTapStart(null), [changeTapStart]);

  const dragPreview =
    preview ??
    (tapStart ? { userId: tapStart.userId, startIdx: tapStart.dayIdx, endIdx: tapStart.dayIdx } : null);

  return {
    dragPreview,
    onRowPointerDown,
    /** True between the first and the second tap of a finger selection. */
    isWaitingForLastDay: tapStart !== null,
    cancelTapSelection,
  };
}
